import { useEffect } from "react";

/**
 * Hook to dynamically update SEO tags and JSON-LD schema in the document head.
 * 
 * @param {Object} seoConfig
 * @param {string} seoConfig.title - The document title
 * @param {string} seoConfig.description - The page description
 * @param {string} [seoConfig.keywords] - Keywords list
 * @param {string} [seoConfig.ogImage] - Open Graph image URL
 * @param {string} [seoConfig.ogType] - Open Graph type (e.g. 'website', 'book')
 * @param {Object} [seoConfig.schema] - JSON-LD Schema.org structured data object
 */
export default function useSEO({ title, description, keywords, ogImage, ogType, schema }) {
  useEffect(() => {
    // 1. Update document title
    if (title) {
      document.title = title;
    }

    // Helper to find or create meta tag
    const setMetaTag = (attrName, attrVal, contentVal) => {
      if (!contentVal) return;
      let tag = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attrName, attrVal);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", contentVal);
    };

    // 2. Set Standard Meta Tags
    setMetaTag("name", "title", title);
    setMetaTag("name", "description", description);
    if (keywords) {
      setMetaTag("name", "keywords", keywords);
    }

    // 3. Set Open Graph (Facebook / LinkedIn)
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", window.location.href);
    if (ogImage) {
      setMetaTag("property", "og:image", ogImage);
    }
    if (ogType) {
      setMetaTag("property", "og:type", ogType);
    }

    // 4. Set Twitter Card Tags
    setMetaTag("property", "twitter:title", title);
    setMetaTag("property", "twitter:description", description);
    if (ogImage) {
      setMetaTag("property", "twitter:image", ogImage);
    }

    // 5. Inject / update JSON-LD Structured Data for search engines & LLMs
    let scriptId = "ld-json-schema";
    let scriptTag = document.getElementById(scriptId);

    if (schema) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = scriptId;
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.innerHTML = JSON.stringify(schema);
    } else {
      // Remove it if no schema provided for this page
      if (scriptTag) {
        scriptTag.remove();
      }
    }

    // Cleanup: Remove schema script tag if unmounting or if configuration changes
    return () => {
      const tagToRemove = document.getElementById(scriptId);
      if (tagToRemove) {
        tagToRemove.remove();
      }
    };
  }, [title, description, keywords, ogImage, ogType, schema]);
}
